export type SeoPageKey = 'what-is-c2pa' | 'is-this-image-ai' | 'image-metadata-viewer' | 'content-credentials-guide';

export type SeoFaq = {
  question: string;
  answer: string;
};

export type SeoSection = {
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

export type SeoPageContent = {
  key: SeoPageKey;
  eyebrow: string;
  title: string;
  answer: string;
  introduction: string;
  proofLabel: string;
  proofItems: Array<{ label: string; value: string }>;
  sections: SeoSection[];
  faqs: SeoFaq[];
  references: Array<{ label: string; url: string }>;
};

export const SEO_PAGES: Record<SeoPageKey, SeoPageContent> = {
  'what-is-c2pa': {
    key: 'what-is-c2pa',
    eyebrow: 'C2PA guide',
    title: 'What is C2PA?',
    answer: 'C2PA is an open technical standard for attaching signed, tamper-evident provenance records to digital content.',
    introduction: 'Those records are commonly displayed as Content Credentials. They can describe where a file came from, which tools handled it and whether the signed record still matches the file you received.',
    proofLabel: 'What a C2PA check can establish',
    proofItems: [
      { label: 'Credential', value: 'Whether supported Content Credentials were found' },
      { label: 'Integrity', value: 'Whether the signed credential still binds to this file' },
      { label: 'Assertions', value: 'Which supported origin and editing declarations are present' },
      { label: 'Boundary', value: 'What was not checked or cannot be concluded' },
    ],
    sections: [
      {
        title: 'C2PA, in plain English',
        paragraphs: [
          'A C2PA credential is closer to a signed history card than a truth label. A participating camera, editing tool or publishing system can add claims about an asset. Cryptographic signatures help a verifier detect whether the credential or its binding to the asset changed later.',
          'The standard makes provenance more inspectable and portable. It does not require you to trust every claim automatically: who signed, which trust policy is active and which parts of the history are present still matter.',
        ],
      },
      {
        title: 'What Content Credentials may contain',
        paragraphs: ['A credential can carry different assertions depending on the creator, device and software involved. A verifier should report only the assertions it actually supports.'],
        bullets: [
          'The organization, device or software that signed the manifest',
          'Creation and editing actions declared by participating tools',
          'Ingredients or source assets referenced by the manifest',
          'AI-related declarations when a participating tool includes them',
        ],
      },
      {
        title: 'What C2PA cannot prove',
        paragraphs: [
          'C2PA does not prove that a picture is factually true, ethically created, legally owned or free from manipulation. It records provenance evidence; it does not replace human judgment, forensic review or legal clearance.',
          'No credential is also not a verdict. Metadata can be stripped by screenshots, exports, social platforms and ordinary file transformations. The honest result may be unknown.',
        ],
      },
    ],
    faqs: [
      { question: 'Is C2PA the same as Content Credentials?', answer: 'C2PA is the technical standard. Content Credentials is the consumer-facing way compatible provenance information is commonly presented.' },
      { question: 'Does every image contain C2PA data?', answer: 'No. Many cameras, editors and publishing systems do not yet add it, and ordinary transformations can remove it.' },
      { question: 'Can I check C2PA data for free?', answer: 'Yes. ORIGNTD inspects supported C2PA data in JPEG and PNG files locally in your browser by default.' },
    ],
    references: [
      { label: 'C2PA 2.2 explainer', url: 'https://c2pa.org/specifications/specifications/2.2/explainer/Explainer.html' },
      { label: 'Content Credentials', url: 'https://contentcredentials.org/' },
    ],
  },
  'is-this-image-ai': {
    key: 'is-this-image-ai',
    eyebrow: 'Evidence before verdict',
    title: 'Is this image AI-generated?',
    answer: 'Sometimes provenance can provide an explicit AI-related declaration. Without supported evidence, an honest checker cannot turn uncertainty into a yes or no.',
    introduction: 'ORIGNTD does not guess from visual style. It inspects supported Content Credentials and metadata, then separates evidence found, evidence not checked and unresolved questions.',
    proofLabel: 'How to read the result',
    proofItems: [
      { label: 'AI declaration found', value: 'Evidence supports that bounded declaration' },
      { label: 'Credential, no AI declaration', value: 'Does not prove the image is human-made' },
      { label: 'No credential found', value: 'Origin remains unknown, not human by default' },
      { label: 'Detector score', value: 'A probability estimate, not provenance proof' },
    ],
    sections: [
      {
        title: 'Start with provenance, not appearance',
        paragraphs: [
          'Images can look synthetic and be human-made, or look photographic and be generated. Compression, retouching and screenshots can also confuse visual classifiers. A provenance check asks a different question: what origin evidence travels with this file?',
          'When a valid Content Credential includes a supported AI-related assertion, that is attributable evidence. The checker should show the assertion, its signer and the integrity state rather than collapse everything into a mysterious score.',
        ],
      },
      {
        title: 'Why “nothing found” is not “human-made”',
        paragraphs: [
          'A file may never have received Content Credentials. It may also have lost them during export, cropping, social sharing or screenshotting. For that reason, absence of supported provenance cannot establish human authorship.',
          'ORIGNTD preserves this distinction because a confident-looking false negative can be more harmful than an explicit unknown.',
        ],
      },
      {
        title: 'Use multiple evidence layers for high-stakes decisions',
        paragraphs: ['For journalism, investigations, rights clearance or safety decisions, combine provenance with source verification, reverse-image search, context, forensic analysis and direct confirmation from the publisher.'],
      },
    ],
    faqs: [
      { question: 'Can metadata prove an image was generated by AI?', answer: 'It can support that conclusion when an attributable declaration is present. Missing or editable metadata cannot prove the opposite.' },
      { question: 'Are AI image detectors always accurate?', answer: 'No. Classifiers can produce false positives and false negatives, especially after editing, compression or model changes.' },
      { question: 'What does ORIGNTD do differently?', answer: 'It reports supported provenance evidence and its limits instead of presenting an unsupported probability as certainty.' },
    ],
    references: [
      { label: 'C2PA 2.2 explainer', url: 'https://c2pa.org/specifications/specifications/2.2/explainer/Explainer.html' },
      { label: 'Content Authenticity Initiative — getting started', url: 'https://opensource.contentauthenticity.org/docs/getting-started/' },
    ],
  },
  'image-metadata-viewer': {
    key: 'image-metadata-viewer',
    eyebrow: 'Local image inspection',
    title: 'View image metadata without uploading it',
    answer: 'ORIGNTD reads a deliberately bounded set of C2PA, EXIF, XMP and IPTC signals from supported JPEG and PNG files in your browser.',
    introduction: 'The free viewer shows what it inspected, what it found and which questions remain outside the scan. Default verification stays on your device.',
    proofLabel: 'Metadata layers inspected',
    proofItems: [
      { label: 'C2PA', value: 'Supported Content Credentials and binding integrity' },
      { label: 'EXIF', value: 'Selected camera and capture fields when present' },
      { label: 'XMP', value: 'Selected descriptive and workflow fields when present' },
      { label: 'IPTC', value: 'Selected editorial fields when present' },
    ],
    sections: [
      {
        title: 'How to inspect an image',
        paragraphs: ['Choose a JPEG or PNG file in the free checker. ORIGNTD validates the file, inspects supported structures locally and produces a bounded evidence report.'],
        bullets: [
          'No account is required for the free local check',
          'JPEG and PNG are supported, up to 25 MB and 40 megapixels',
          'The report keeps found, missing, failed and not-checked states separate',
          'An external provider is contacted only after a separate explicit confirmation',
        ],
      },
      {
        title: 'Metadata is evidence, not automatic truth',
        paragraphs: [
          'Ordinary EXIF, XMP and IPTC fields can be changed or removed. They are useful observations, but they do not carry the same integrity guarantees as a valid signed credential.',
          'ORIGNTD therefore shows the source and status of each supported signal instead of treating all metadata as equally trustworthy.',
        ],
      },
      {
        title: 'Why metadata may be missing',
        paragraphs: ['Screenshots, messaging apps, social networks, optimization pipelines and privacy tools often strip metadata. A blank field does not prove that the event, device or author did not exist.'],
      },
    ],
    faqs: [
      { question: 'Is the image uploaded to ORIGNTD?', answer: 'The default verification path runs locally in your browser. Optional external checks require a separate explicit confirmation.' },
      { question: 'Can I view metadata on a phone?', answer: 'Yes, if the browser can provide a supported JPEG or PNG within the file-size and image-dimension limits.' },
      { question: 'Why is some metadata marked unknown?', answer: 'Unknown means the available evidence did not support a conclusion. It is intentionally different from absent or false.' },
    ],
    references: [
      { label: 'C2PA 2.2 explainer', url: 'https://c2pa.org/specifications/specifications/2.2/explainer/Explainer.html' },
      { label: 'Content Authenticity Initiative — getting started', url: 'https://opensource.contentauthenticity.org/docs/getting-started/' },
    ],
  },
  'content-credentials-guide': {
    key: 'content-credentials-guide',
    eyebrow: 'Content Credentials guide',
    title: 'How to find and verify Content Credentials',
    answer: 'Content Credentials can expose signed provenance information about a digital asset, including supported origin, editing and AI-related assertions.',
    introduction: 'A useful verification checks more than whether a badge exists. It examines the credential, its binding to the file, the signer and the limits of the available evidence.',
    proofLabel: 'A responsible verification sequence',
    proofItems: [
      { label: '1 · Find', value: 'Look for a supported credential or manifest' },
      { label: '2 · Validate', value: 'Check the cryptographic binding to this file' },
      { label: '3 · Read', value: 'Inspect supported assertions and signer information' },
      { label: '4 · Bound', value: 'State what the credential does not establish' },
    ],
    sections: [
      {
        title: 'What to look for in a result',
        paragraphs: ['A verifier should expose the evidence behind its summary. The most useful result makes each layer inspectable rather than reducing the asset to a single green or red badge.'],
        bullets: [
          'Whether a Content Credential was found',
          'Whether the credential-to-file binding validates',
          'Which entity or tool signed the manifest, when available',
          'Which supported actions, ingredients or declarations appear',
          'Which checks were unavailable, unsupported or inconclusive',
        ],
      },
      {
        title: 'Valid does not mean true',
        paragraphs: [
          'A valid signature can establish that a particular signer made particular claims and that the signed data still binds to the file. It does not independently establish that the depicted event happened, that every claim is accurate or that the signer owns all rights.',
          'Trust policy, source context and the completeness of the provenance chain remain separate questions.',
        ],
      },
      {
        title: 'When credentials are missing',
        paragraphs: ['Treat the result as unresolved unless another evidence source supports a conclusion. Missing credentials can reflect non-participating tools, stripped metadata or ordinary transformations—not necessarily deception.'],
      },
    ],
    faqs: [
      { question: 'Where are Content Credentials stored?', answer: 'They may be embedded in the file or recoverable through supported external mechanisms, depending on how the asset was created and transformed.' },
      { question: 'Can Content Credentials survive screenshots?', answer: 'Embedded data usually does not travel into a screenshot. Recovery may still be possible only when a supported external identity or repository path exists.' },
      { question: 'Does a valid credential prove authenticity?', answer: 'It supports bounded provenance and integrity claims. It does not by itself prove factual truth, ownership or legal clearance.' },
    ],
    references: [
      { label: 'Content Credentials — adoption resources', url: 'https://contentcredentials.org/adopt/' },
      { label: 'C2PA 2.2 explainer', url: 'https://c2pa.org/specifications/specifications/2.2/explainer/Explainer.html' },
    ],
  },
};

export const SEO_PAGE_KEYS = Object.keys(SEO_PAGES) as SeoPageKey[];

export function getSeoPageFromPath(pathname: string): SeoPageContent | undefined {
  const key = pathname.replace(/^\/+|\/+$/g, '') as SeoPageKey;
  return SEO_PAGES[key];
}
